import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { useSession } from '../contexts/SessionContext';
import { getWizardSteps } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_LEAVE_HINT, WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { waitForSessionUpdate } from '../lib/navigationHelpers';
import { supabase } from '../lib/supabase';
import { logGenerationError, resolveTimeoutErrors } from '../lib/errorLogger';
import { fetchWithHeadStartRetry } from '../lib/fetchWithHeadStartRetry';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;
const POLLING_MAX_ATTEMPTS = 120;
const POLLING_INTERVAL = 2000;

export function ArticleGenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, currentIdea, refreshSession } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);
  const isMountedRef = useRef(true);
  const attemptedForSessionRef = useRef<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Gebruikersprofiel ophalen', status: 'pending' },
    { label: 'Prompt template laden', status: 'pending' },
    { label: 'AI artikel schrijven', status: 'pending' },
    { label: 'Artikel opslaan in database', status: 'pending' },
    { label: 'Sessie bijwerken', status: 'pending' },
  ]);

  const updateStepStatus = (stepIndex: number, status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  const sessionId = sessionIdFromLocation(location);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sessionId: string) => {
    try {
      setLoading(true);
      console.log('Loading session from database:', sessionId);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError) {
        console.error('Error loading session:', sessionError);
        setError('Kon sessie niet laden');
        setLoading(false);
        return;
      }

      if (!sessionData) {
        console.error('Session not found');
        setError('Sessie niet gevonden');
        setLoading(false);
        return;
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      if (userError || !userData) {
        console.error('Error loading user:', userError);
        setError('Kon gebruiker niet laden');
        setLoading(false);
        return;
      }

      const { data: ideaData, error: ideaError } = await supabase
        .from('blog_ideas')
        .select('*')
        .eq('id', sessionData.approved_idea_id)
        .maybeSingle();

      if (ideaError || !ideaData) {
        console.error('Error loading idea:', ideaError);
        setError('Kon blog idee niet laden');
        setLoading(false);
        return;
      }

      setDashboardSession(sessionData);
      setDashboardUser(userData);
      setDashboardIdea(ideaData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setError('Er ging iets mis bij het laden van de sessie');
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const activeUser = dashboardUser || user;
  const activeIdea = dashboardIdea || currentIdea;

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (!activeSession || !activeUser || !activeIdea) return;
    if (isGenerating) return;
    if (attemptedForSessionRef.current === activeSession.id) return;

    // Artikel bestaat al (terug via browser of bolletje): meteen door naar de editor.
    // replace: dit scherm blijft niet in de history staan, anders loopt Terug in een lus.
    if (activeSession.article_content) {
      navigate(wizardUrl(WIZARD_PATHS.articleEdit, activeSession.id), { replace: true });
      return;
    }

    // Check if we already attempted generation for this session (survives page refresh)
    const attemptKey = `article_gen_attempted_${activeSession.id}`;
    const alreadyAttempted = sessionStorage.getItem(attemptKey);

    // Poll- en doorstuurstaart: wacht tot het artikel in de database staat en stuur dan
    // door naar de editor. Gedeeld door de normale generatie hieronder en door het
    // hervatten van een generatie die de server nog aan het schrijven is (zie onder).
    const waitForArticleAndContinue = async () => {
      const verified = await waitForSessionUpdate(
        activeSession.id,
        'article_content',
        POLLING_MAX_ATTEMPTS,
        POLLING_INTERVAL
      );

      if (!verified) {
        updateStepStatus(2, 'error');
        updateStepStatus(3, 'error');
        updateStepStatus(4, 'error');

        await logGenerationError({
          generationType: 'blog_content',
          errorCategory: 'TIMEOUT',
          errorMessage: 'Article werd niet gegenereerd binnen verwachte tijd',
          retryAttempt: 0,
          sessionId: activeSession.id,
          additionalContext: {
            service: 'openai',
            apiType: 'article_generation_polling',
            pollingAttempts: POLLING_MAX_ATTEMPTS,
            pollingInterval: POLLING_INTERVAL,
          },
        });

        throw new Error('Artikel werd niet gegenereerd. Dit kan betekenen dat de AI server overbelast is. Probeer het later opnieuw.');
      }

      // Status doorzetten zodra het artikel er is; alleen vanuit 'idea_approved', nooit terug.
      await supabase
        .from('sessions')
        .update({ state: 'article_generated' })
        .eq('id', activeSession.id)
        .eq('state', 'idea_approved');

      updateStepStatus(2, 'completed');
      updateStepStatus(3, 'completed');
      updateStepStatus(4, 'completed');

      console.log('[ArticleGenerate] Article successfully generated and saved!');

      await resolveTimeoutErrors(activeSession.id);

      if (refreshSession) {
        await refreshSession();
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      if (!isMountedRef.current) {
        console.log('Component unmounted, skipping navigation');
        return;
      }

      console.log('[ArticleGenerate] Navigating to article edit...');
      navigate(wizardUrl(WIZARD_PATHS.articleEdit, activeSession.id), { replace: true });
      setIsGenerating(false);
    };

    if (alreadyAttempted) {
      console.log('[ArticleGenerate] Already attempted generation for this session, skipping');
      const savedError = sessionStorage.getItem(`${attemptKey}_error`);
      if (savedError) {
        setError(savedError);
        return;
      }

      // Geen opgeslagen fout: de server is het artikel waarschijnlijk nog aan het schrijven
      // (bijv. na een refresh midden in de generatie). Wacht het resultaat af in plaats van
      // meteen een foutmelding te tonen.
      const resumeWaitingForArticle = async () => {
        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');
        updateStepStatus(3, 'in_progress');
        updateStepStatus(4, 'in_progress');
        attemptedForSessionRef.current = activeSession.id;
        setIsGenerating(true);

        try {
          await waitForArticleAndContinue();
        } catch (err) {
          console.error('[ArticleGenerate] Error while waiting for in-progress article:', err);
          setError('Artikel werd niet gegenereerd. Dit kan betekenen dat de AI server overbelast is. Probeer het later opnieuw.');
          setIsGenerating(false);
        }
      };

      resumeWaitingForArticle();
      return;
    }

    // Validate session data before attempting generation
    if (!activeSession.approved_idea_id) {
      console.error('[ArticleGenerate] Session has no approved_idea_id');
      setError('Sessie heeft geen goedgekeurd blog idee. Ga terug naar het dashboard en start opnieuw.');
      return;
    }

    if (!activeIdea.id || activeIdea.id !== activeSession.approved_idea_id) {
      console.error('[ArticleGenerate] Idea mismatch:', {
        activeIdeaId: activeIdea.id,
        approvedIdeaId: activeSession.approved_idea_id
      });
      setError('Blog idee kon niet worden geladen of komt niet overeen met de goedgekeurde blog. Ga terug naar het dashboard.');
      return;
    }

    if (!activeIdea.idea_title || !activeIdea.idea_content) {
      console.error('[ArticleGenerate] Idea missing title or content:', activeIdea);
      setError('Blog idee is incompleet (geen titel of inhoud). Ga terug naar het dashboard en kies een ander idee.');
      return;
    }

    const generateArticle = async () => {
      attemptedForSessionRef.current = activeSession.id;
      sessionStorage.setItem(attemptKey, 'true');
      setIsGenerating(true);

      try {
        console.log('[ArticleGenerate] Starting article generation...');

        updateStepStatus(0, 'in_progress');
        updateStepStatus(1, 'in_progress');

        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

        console.log('[ArticleGenerate] Sending generation request to Edge Function...');

        type ServerErrorBody = { message?: string; error?: string; logged?: boolean; category?: string };

        // Head-start fetch met stille retry: een snelle netwerk-reject (verbinding
        // hapert even) wordt één keer opnieuw geprobeerd voordat we falen, zodat
        // de gebruiker daar niks van merkt. Blijft de fetch hangen, dan verwerkt
        // de server hem en gaan we verder met DB-polling.
        const { networkError, response } = await fetchWithHeadStartRetry(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: 'article',
              session_id: activeSession.id,
              user_id: activeUser.id,
              data: {
                idea_title: activeIdea.idea_title,
                idea_content: activeIdea.idea_content,
                article_direction: activeSession.article_direction || '',
                article_focus: activeSession.article_focus || '',
              },
            }),
          },
          { headStartMs: 3000, maxRetries: 1, backoffMs: 1500 },
        );

        let errorBody: ServerErrorBody | null = null;
        if (response && !response.ok) {
          try {
            errorBody = await response.json() as ServerErrorBody;
          } catch {
            errorBody = { error: `HTTP ${response.status}` };
          }
          console.error('[ArticleGenerate] Server returned error:', errorBody);
        }

        if (networkError) {
          console.error('[ArticleGenerate] Network error (na retry):', networkError);
          updateStepStatus(0, 'error');
          updateStepStatus(1, 'error');
          const e = new Error(
            'Kon het verzoek niet verzenden. Controleer je internetverbinding en probeer het opnieuw.'
          ) as Error & { category?: string };
          e.category = 'NETWORK_ERROR';
          throw e;
        }

        if (errorBody) {
          updateStepStatus(0, 'error');
          updateStepStatus(1, 'error');
          const body = errorBody;
          const e = new Error(
            body.message || body.error || 'Server-fout bij artikel-generatie'
          ) as Error & { serverLogged?: boolean; category?: string };
          e.serverLogged = body.logged === true;
          e.category = body.category;
          throw e;
        }

        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');
        updateStepStatus(3, 'in_progress');
        updateStepStatus(4, 'in_progress');

        console.log('[ArticleGenerate] Request sent! Polling database for article_content...');

        await waitForArticleAndContinue();
      } catch (err) {
        console.error('[ArticleGenerate] Error during generation:', err);

        const errAny = err as Error & { serverLogged?: boolean; category?: string };

        // Skip client-side log als server al een user_errors-row schreef.
        if (!errAny.serverLogged) {
          await logGenerationError({
            generationType: 'blog_content',
            errorCategory: (errAny.category as 'TIMEOUT' | 'NETWORK_ERROR' | 'API_ERROR' | 'UNKNOWN') || 'UNKNOWN',
            errorMessage: err instanceof Error ? err.message : 'Unknown error',
            technicalError: err instanceof Error ? err.stack : undefined,
            retryAttempt: 0,
            sessionId: activeSession?.id,
            additionalContext: {
              service: 'openai',
              apiType: 'article_generation_polling',
            },
          });
        }

        const errorMsg = `Er ging iets mis: ${err instanceof Error ? err.message : 'Unknown error'}`;
        sessionStorage.setItem(`${attemptKey}_error`, errorMsg);
        setError(errorMsg);
        setIsGenerating(false);
      }
    };

    generateArticle();
  }, [activeSession, activeUser, activeIdea, loading, isGenerating]);

  useEffect(() => {
    if (activeSession) {
      console.log('ArticleGenerate - Current session state:', {
        state: activeSession.state,
        approved_idea_id: activeSession.approved_idea_id,
        currentIdea: activeIdea?.id
      });
    }
  }, [activeSession, activeIdea]);

  const steps = getWizardSteps(activeSession, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Artikel genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!activeSession || !activeUser || !activeIdea) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Artikel genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <h2 className="text-xl font-bold text-red-800 mb-2">Geen blog idee gevonden</h2>
            <p className="text-red-700 mb-4">Er is geen actieve sessie of goedgekeurd idee beschikbaar.</p>
            <div className="text-sm text-red-600 mb-4">
              <p className="font-semibold">Debug info:</p>
              <ul className="list-disc list-inside mt-2">
                <li>Sessie: {activeSession ? 'Aanwezig' : 'Niet gevonden'}</li>
                <li>Gebruiker: {activeUser ? 'Aanwezig' : 'Niet gevonden'}</li>
                <li>Blog idee: {activeIdea ? 'Aanwezig' : 'Niet gevonden'}</li>
                {activeSession && (
                  <li>Approved idea ID: {activeSession.approved_idea_id || 'Geen'}</li>
                )}
              </ul>
            </div>
            <button
              onClick={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
              className="btn-secondary"
            >
              Naar Mijn Blogs
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Artikel genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <StepIndicator steps={steps} interactive={false} />
          <div className="card bg-red-50 border-red-200 mt-6">
            <h2 className="text-xl font-bold text-red-800 mb-3">Er ging iets mis</h2>
            <p className="text-red-800 mb-4">{error}</p>
            <div className="bg-red-100 border border-red-300 rounded p-3 mb-4 text-sm text-red-700">
              <p className="font-semibold mb-1">Belangrijk:</p>
              <p>Ververs deze pagina NIET opnieuw. Dit voorkomt dat de fout meerdere keren wordt gelogd.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
                className="btn-primary"
              >
                Naar Mijn Blogs
              </button>
              <button
                onClick={() => {
                  // Clear attempt flag to allow retry
                  if (activeSession?.id) {
                    sessionStorage.removeItem(`article_gen_attempted_${activeSession.id}`);
                    sessionStorage.removeItem(`article_gen_attempted_${activeSession.id}_error`);
                  }
                  window.location.reload();
                }}
                className="btn-secondary"
              >
                Probeer opnieuw
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Artikel genereren"
        onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <StepIndicator steps={steps} interactive={false} />
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
            We schrijven je artikel...
          </h1>
          <p className="text-gray-600">Dit kan enkele minuten duren.</p>
          <p className="text-sm text-gray-500">{WIZARD_LEAVE_HINT}</p>
        </div>
        <StepProgressIndicator steps={progressSteps} />
      </div>
      </main>
    </div>
  );
}
